/**
 * LoopHero — composes the whole morning loop into the ONE TodayHero card
 * (direction §1) and owns the completion celebration (direction §5).
 *
 * It takes the already-composed TodayHero display props and nests the
 * reward strip, the CTA, the reserved readiness slot, and the one coach
 * voice line into the hero via its slot props.
 *
 * The reward payoff is **optimistic** — mandatory per audit B1.1, where
 * the streak stayed `0d` until reload because it only recomputed
 * server-side. On a successful (non-undo) toggle we immediately:
 *   - fill today's days-done segment + tick the streak (optimistic state),
 *   - run the ~520ms beat (segPop · streakPop · --today ripple · coach
 *     crossfade to an acknowledgment),
 * then reconcile when the parent's `weekly`/`seasonPlans` refetch lands
 * (new `days`/`streakDays` props → optimistic overrides cleared).
 *
 * `prefers-reduced-motion` is handled in the leaf components' CSS: the
 * state changes (fill, count, recolor) stay; the springs + ripple drop.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Day } from '../../fixtures/plan';
import { heroSublineWithUnit, type DistanceUnit } from '../../lib/actualForPlanDay';
import type { ProactiveTriggerType } from '../../lib/proactiveCopy';
import { heroStatLine, parseHeroTitle, tomorrowSessionLine } from '../../lib/todayHeroAdapter';
import { TayroMarkCelebration } from '../brand/TayroMarkCelebration';
import { TodayHero, type TodayHeroDefaultProps } from '../TodayHero/TodayHero';
import { CoachLine } from './CoachLine';
import { MarkTodayCompleteButton } from './MarkTodayCompleteButton';
import { HealthHomeSlot } from './HealthHomeSlot';
import { RewardStrip } from './RewardStrip';
import { HeroActionsV2 } from './v2/HeroActionsV2';
import styles from './LoopHero.module.css';

/** How long the celebration beat runs before the springs/ripple clear. A
 *  touch longer than --reward-ripple-dur (620ms) so the ring finishes. */
const CELEBRATE_MS = 700;

/** E2-b — true when completing `dayId` finishes the WEEK: every other
 *  non-rest day is already completed ('skipped' doesn't count). Gates the
 *  logo celebration to the week-final moment, not every day-complete. */
export function completesWeek(days: Day[], dayId: string): boolean {
  const trainingDays = days.filter((d) => !d.rest);
  if (trainingDays.length === 0) return false;
  return trainingDays.every((d) => (d.id === dayId ? true : d.completionStatus === 'completed'));
}

export interface LoopHeroProps {
  /** Composed TodayHero display props (eyebrow/title/meta/phase/footer).
   *  Slots are filled here, so callers must NOT set them. */
  heroProps: TodayHeroDefaultProps;
  /** Today's Day (the CTA target + the ring's today segment). */
  today: Day | null;
  /** This week's days (drives the days-done ring). */
  days: Day[];
  /** Canonical current streak (`computeCurrentStreak`). */
  streakDays: number;
  weekMileageDone: number;
  weekMileageTarget: number;
  /** B332 / B314 — the unit `weekMileageDone` is stated in. Omitted = 'mi'. */
  distanceUnit?: DistanceUnit;
  /** B330 — the week is on plan (false hides the reward strip's on-track line). */
  weekOnTrack?: boolean;
  onToggleDay?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /**
   * RPE restore — reopen the effort prompt for today (the recovery path
   * when the post-completion prompt was skipped). Renders the "Add
   * effort" ghost button beside the completed CTA when today is
   * completed without an RPE.
   */
  onAddEffort?: (dayId: string, workoutLabel: string) => void;
  /** Coach voice — the daily motivation line + optional proactive trigger. */
  coachName?: string;
  coachLine: string | null;
  coachHref?: string;
  proactiveType?: ProactiveTriggerType | null;
  /** SP-8 (today_density_v1) — reward-strip mobile micro-trims (decision D4).
   *  Threaded to <RewardStrip dense>. Default undefined = byte-identical. */
  dense?: boolean;
  /**
   * W1-TODAY-HERO (spec 7.4) — `'v2'` is the 2.0 hero. WIRE passes this and
   * nothing else; the date, kicker, session name, stat line, day text, action,
   * done lines and rest-day Tomorrow row are all derived here from `heroProps`
   * (date, title, meta, weatherAttribution, phase, weekOfTotal), `today` and
   * `days`. No reward strip, ring, streak chip or status footer renders in v2; the
   * optimistic toggle, its rollback and the week-final celebration are the same
   * code as the legacy hero. Absent = the legacy hero, byte for byte.
   */
  variant?: 'v2';
}

type Optimistic = { completed: boolean; streak: number; days: Day[] };

export function LoopHero({
  heroProps,
  today,
  days,
  streakDays,
  weekMileageDone,
  weekMileageTarget,
  distanceUnit,
  weekOnTrack,
  onToggleDay,
  onAddEffort,
  coachName,
  coachLine,
  coachHref,
  proactiveType,
  dense,
  variant,
}: LoopHeroProps) {
  const [optimistic, setOptimistic] = useState<Optimistic | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [logoCelebrating, setLogoCelebrating] = useState(false);
  const timerRef = useRef<number | null>(null);

  // Reconcile — when the canonical data changes (the refetch landed), drop
  // the optimistic overrides. streakDays is a number (compares by value);
  // `days` is stable until the weekly refetch returns a new array.
  useEffect(() => {
    setOptimistic(null);
  }, [days, streakDays]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const effectiveDays = optimistic ? optimistic.days : days;
  const effectiveStreak = optimistic ? optimistic.streak : streakDays;
  const effectiveToday: Day | null =
    optimistic && today
      ? { ...today, completionStatus: optimistic.completed ? 'completed' : 'upcoming' }
      : today;

  const handleToggle = useCallback(
    async (dayId: string, currentStatus: Day['completionStatus']) => {
      const completing = currentStatus !== 'completed';
      const optDays = days.map((d) =>
        d.id === dayId
          ? { ...d, completionStatus: (completing ? 'completed' : 'upcoming') as Day['completionStatus'] }
          : d,
      );
      const optStreak = completing ? streakDays + 1 : Math.max(0, streakDays - 1);
      setOptimistic({ completed: completing, streak: optStreak, days: optDays });

      // Only completing is a celebration; undo is a quiet correction.
      if (completing) {
        setCelebrating(true);
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setCelebrating(false), CELEBRATE_MS);
        // E2-b — the logo celebration is reserved for the week-final
        // completion (motion spec: "weekly completion", once, never loops).
        if (completesWeek(days, dayId)) setLogoCelebrating(true);
      }

      if (onToggleDay) {
        try {
          await onToggleDay(dayId, currentStatus);
        } catch {
          // Roll back the optimistic beat on failure; the canonical data
          // is unchanged so the UI returns to its pre-tap state.
          setOptimistic(null);
          setCelebrating(false);
          setLogoCelebrating(false);
          if (timerRef.current) window.clearTimeout(timerRef.current);
        }
      }
    },
    [days, streakDays, onToggleDay],
  );

  const hasCta = !!effectiveToday?.id && !effectiveToday.rest && !!onToggleDay;
  const acknowledgment = `That's ${effectiveStreak} in a row, keep it rolling.`;

  // B-19 (adversarial LOW #2 — undo race) — the completed-state sub-line must
  // track the SAME optimistic status the CTA/reward use, not the parent's stale
  // (pre-refetch) status. `heroProps.completedSubline` was derived upstream from
  // the non-optimistic `today.completionStatus`; without this guard a tap-to-undo
  // reverts the CTA instantly but the sub-line lingers until the refetch lands.
  // Render-side only — the data path (useWeeklyPlan → actual.hero) is unchanged.
  const completedSubline =
    effectiveToday?.completionStatus === 'completed' ? heroProps.completedSubline : undefined;

  if (variant === 'v2') {
    const parts = parseHeroTitle(heroProps.title);
    const isRest = !!effectiveToday?.rest;
    const isDone = effectiveToday?.completionStatus === 'completed';
    // D7: "You ran" only where today's actuals gate already allows it. That gate
    // decides whether the Day carries `actual.hero` at all (b19_actuals_v1 plus a
    // stored actual); this reads the Day, so it holds with density on or off.
    const ran = isDone
      ? heroSublineWithUnit(
          effectiveToday?.actual?.hero,
          effectiveToday?.mileageInUnit ?? effectiveToday?.mileage,
        )
      : null;
    // The day's own text. Frames 1e to 1g (done) carry none, and a rest day has its own line.
    const dayText = !isRest && !isDone ? effectiveToday?.details?.trim() || null : null;
    const canAddEffort = isDone && !celebrating && !!effectiveToday?.id && !!onAddEffort;

    return (
      <TodayHero
        variant="v2"
        date={heroProps.date}
        weekOfTotal={heroProps.weekOfTotal}
        phase={heroProps.phase}
        title={parts.name}
        stat={isRest ? null : heroStatLine(parts, effectiveToday?.mileage)}
        completedLine={ran?.text ?? null}
        effortLine={
          isDone && typeof effectiveToday?.rpe === 'number' ? `Effort ${effectiveToday.rpe} of 10` : null
        }
        dayText={dayText}
        restLine={isRest ? 'Nothing to mark today.' : null}
        tomorrow={isRest ? tomorrowSessionLine(days) : null}
        meta={heroProps.meta}
        weatherAttribution={heroProps.weatherAttribution}
        actions={
          hasCta && effectiveToday ? (
            <HeroActionsV2
              day={effectiveToday}
              onToggle={handleToggle}
              effortAction={
                canAddEffort ? (
                  <button
                    type="button"
                    className={styles.v2Effort}
                    onClick={() => onAddEffort!(effectiveToday.id!, effectiveToday.label)}
                  >
                    {effectiveToday.rpe == null ? 'Add effort' : 'Edit effort'}
                  </button>
                ) : undefined
              }
            />
          ) : undefined
        }
        coachLine={
          <>
            <CoachLine
              variant="v2"
              coachName={coachName}
              line={coachLine}
              href={coachHref}
              proactiveType={proactiveType}
            />
            {/* W1-TODAY-FIX1 (F2, D11): the never-synced Apple Health connect entry stays
                on Today. With `v2` the slot draws only that state (the stale state is the
                status row's) and nothing at all when synced, on web or with the flag off. */}
            <HealthHomeSlot v2 />
          </>
        }
        overlay={
          logoCelebrating ? (
            <div className={styles.v2Celebration} aria-hidden>
              <TayroMarkCelebration size={56} onDone={() => setLogoCelebrating(false)} />
            </div>
          ) : undefined
        }
      />
    );
  }

  return (
    <TodayHero
      {...heroProps}
      completedSubline={completedSubline}
      readiness={<HealthHomeSlot />}
      reward={
        <div className={styles.rewardWrap}>
          <RewardStrip
            streakDays={effectiveStreak}
            days={effectiveDays}
            weekMileageDone={weekMileageDone}
            weekMileageTarget={weekMileageTarget}
            unit={distanceUnit}
            onTrack={weekOnTrack}
            celebrating={celebrating}
            dense={dense}
          />
          {logoCelebrating && (
            <div className={styles.logoCelebration} aria-hidden>
              <TayroMarkCelebration size={56} onDone={() => setLogoCelebrating(false)} />
            </div>
          )}
        </div>
      }
      cta={
        hasCta ? (
          <div className={styles.ctaWrap}>
            <MarkTodayCompleteButton day={effectiveToday} onToggle={handleToggle} />
            {/* RPE restore — recovery path: today completed (and not
                mid-celebration: the auto-prompt owns that moment). Sibling
                button, never nested. F-110(d): the affordance STAYS once an RPE
                exists — it flips to "Edit effort" so founders can log a
                correction (reopens the same capture sheet, prefilled). */}
            {effectiveToday?.completionStatus === 'completed' &&
              !celebrating &&
              effectiveToday.id &&
              onAddEffort && (
                <button
                  type="button"
                  className={styles.addEffort}
                  onClick={() => onAddEffort(effectiveToday.id!, effectiveToday.label)}
                >
                  {effectiveToday.rpe == null ? 'Add effort' : 'Edit effort'}
                </button>
              )}
            {celebrating && <span className={styles.ripple} aria-hidden />}
          </div>
        ) : undefined
      }
      coachLine={
        <CoachLine
          coachName={coachName}
          line={coachLine}
          href={coachHref}
          proactiveType={proactiveType}
          celebrating={celebrating}
          acknowledgment={acknowledgment}
        />
      }
    />
  );
}
